import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Badge,
  Row,
  Col,
  Card,
  Spinner,
  Dropdown,
  DropdownButton,
} from "react-bootstrap";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../utils/constant";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import Pagination from "../../components/Pagination";
import { BsThreeDotsVertical } from "react-icons/bs";

export default function Support() {
  const role = localStorage.getItem("role");
  const userId = localStorage.getItem("userId");

  const [supportType, setSupportType] = useState("");
  const [heading, setHeading] = useState("");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchTickets(currentPage);
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchTickets(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [search]);

  const fetchTickets = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/users/help-resources/`, {
        headers: HEADERS,
        params: {
          page,
          page_size: itemsPerPage,
          search,
        },
      });

      setTickets(res.data?.results || []);
      setTotalPages(res.data?.total_pages || 0);
      setCurrentPage(page);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supportType || !heading || !description) {
      toast.error("All fields are required");
      return;
    }

    const formData = new FormData();
    formData.append("support_type", supportType);
    formData.append("heading", heading);
    formData.append("description", description);
    if (attachment) formData.append("attachment", attachment);
    if (userId) formData.append("userId", userId);

    setSubmitLoading(true);

    try {
      await axios.post(`${BASE_URL}/users/help-resources/`, formData, {
        headers: { ...HEADERS, "Content-Type": "multipart/form-data" },
      });

      toast.success("Request submitted successfully");

      setSupportType("");
      setHeading("");
      setDescription("");
      setAttachment(null);

      fetchTickets(1);
    } catch {
      // toast.error("Submission failed");
    } finally {
      setSubmitLoading(false);
    }
  };

  const markResolved = async (id) => {
    try {
      await axios.put(
        `${BASE_URL}/users/help-resources/${id}/`,
        { status: "completed" },
        { headers: HEADERS },
      );

      toast.success("Marked as completed");
      fetchTickets(currentPage);
    } catch {
      // toast.error("Update failed");
    }
  };

  const getBadgeVariant = (status) => {
    if (status === "pending") return "warning";
    if (status === "completed") return "success";
    return "secondary";
  };

  return (
    <div className="container-fluid">
      <div className="mt-3 d-flex justify-content-between align-items-center mb-3">
        <h4>Help & Support</h4>

        <Form.Control
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "250px" }}
        />
      </div>
      {role !== "superadmin" && (
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <h5>Create Request</h5>

            <Form onSubmit={handleSubmit}>
              <fieldset disabled={submitLoading}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Select
                      value={supportType}
                      onChange={(e) => setSupportType(e.target.value)}
                    >
                      <option value="">Select Type</option>
                      <option value="feedback">Feedback</option>
                      <option value="custom">Support</option>
                    </Form.Select>
                  </Col>

                  <Col md={6}>
                    <Form.Control
                      placeholder="Heading"
                      value={heading}
                      onChange={(e) => setHeading(e.target.value)}
                    />
                  </Col>

                  <Col md={12}>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Control
                      type="file"
                      onChange={(e) => setAttachment(e.target.files[0])}
                    />
                  </Col>

                  <Col md={6} className="text-end">
                    <Button type="submit" disabled={submitLoading}>
                      {submitLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                        </>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </Col>
                </Row>
              </fieldset>
            </Form>
          </Card.Body>
        </Card>
      )}
      <div className="table-responsive">
        {loading ? (
          <Loader />
        ) : tickets.length > 0 ? (
          <table className="table tstart">
            <thead className="thead">
              <tr>
                {role === "superadmin" && (
                  <>
                    <th style={{ width: "250px" }}>User</th>
                    <th style={{ width: "250px" }}>Email</th>
                    <th style={{ width: "200px" }}>Phone</th>
                  </>
                )}
                <th style={{ width: "150px" }}>Type</th>
                <th style={{ width: "250px" }}>Heading</th>
                <th style={{ width: "300px" }}>Description</th>
                <th style={{ width: "150px" }}>Status</th>
                <th style={{ width: "150px" }}>Attachment</th>
                {role === "superadmin" && <th style={{ width: "120px" }}></th>}
              </tr>
            </thead>

            <tbody>
              {tickets.map((t, index) => (
                <tr
                  key={t.id}
                  style={{
                    background: index % 2 === 0 ? "#f8f9fa" : "#fff",
                    cursor: "pointer",
                  }}
                >
                  {role === "superadmin" && (
                    <>
                      <td>
                        <div className="fw-semibold">
                          {t.user?.first_name || "-"}
                        </div>
                      </td>

                      <td>{t.user?.email || "-"}</td>
                      <td>{t.user?.phone_number || "-"}</td>
                    </>
                  )}

                  <td>
                    <Badge bg="info">{t.support_type}</Badge>
                  </td>

                  <td className="tdsubject" title={t.heading}>
                    {t.heading}
                  </td>

                  <td className="tdsubject" title={t.description}>
                    {t.description}
                  </td>

                  <td>
                    <Badge bg={getBadgeVariant(t.status)}>{t.status}</Badge>
                  </td>

                  <td>
                    {t.attachment ? (
                      <Button size="sm" href={t.attachment}>
                        View
                      </Button>
                    ) : (
                      "-"
                    )}
                  </td>

                  {role === "superadmin" && (
                    <td>
                      {t.status !== "completed" ? (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            markResolved(t.id);
                          }}
                        >
                          Done
                        </Button>
                      ) : (
                        <span className="text-success fw-semibold">
                          Completed
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <NoData message="No Requests Found" />
        )}
      </div>

      {tickets.length > 0 && (
        <Pagination
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          handlePageClick={(selected) => setCurrentPage(selected.selected + 1)}
          handleItemsPerPageChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  );
}
